/**
 * Shared pure logic for group route guards.
 * No Pinia, no fetch – used by route middleware only.
 */

import { GroupType, MemberStatus } from '@communities/groups-layer/types/types'

import { JOIN_PLANS_ROUTE_SEGMENT } from '@communities/groups-layer/constants/join-plans-route.constants'

import { CONTENT_UNAVAILABLE_SEGMENT, MEMBERSHIP_EMPTY_STATE_SEGMENTS, VALID_GROUP_FIRST_SEGMENTS } from '../constants/group-route-segments'
import type { GroupData, UserMembershipData } from '../services/middleware-data.service'

export interface GroupRouteRedirect {
  redirect: 'home' | 'about' | 'not_found' | 'content_unavailable'
  accessDenied?: boolean
}

export function getGroupHomePath(groupSlug: string, isGokollab: boolean): string {
  return isGokollab ? `/${groupSlug}/home` : `/communities/groups/${groupSlug}/home`
}

export function getGroupAboutPath(groupSlug: string, isGokollab: boolean): string {
  return isGokollab ? `/${groupSlug}/about` : `/communities/groups/${groupSlug}/about`
}

export function getGroupContentUnavailablePath(groupSlug: string, isGokollab: boolean): string {
  return isGokollab ? `/${groupSlug}/${CONTENT_UNAVAILABLE_SEGMENT}` : `/communities/groups/${groupSlug}/${CONTENT_UNAVAILABLE_SEGMENT}`
}

/**
 * URL detection for the banned-member fallback page. The layout uses this to
 * suppress group chrome (channels list, group info panel, top tab bar, join /
 * profile / membership modals) so a banned user sees no group content at all.
 *
 * Matches both routing contexts:
 *  - default:   /communities/groups/<slug>/content-unavailable
 *  - gokollab:  /<slug>/content-unavailable
 *
 * Trailing slashes are tolerated. Returns false when `groupSlug` is missing
 * so unrelated routes (e.g. /communities, /communities/groups) never match.
 */
export function isContentUnavailablePath(path: string, groupSlug: string | null | undefined): boolean {
  if (!path || !groupSlug) return false
  const normalized = path.replace(/\/+$/, '')
  const segments = normalized.split('/').filter(Boolean)
  const normalizedGroupSlug = normalizeGroupRouteSlug(groupSlug)

  // /communities/groups/[groupSlug]/content-unavailable
  const isDefaultCommunitiesPath = segments.length === 4 && segments[0] === 'communities' && segments[1] === 'groups'
  if (isDefaultCommunitiesPath) {
    const pathGroupSlug = normalizeGroupRouteSlug(segments[2] ?? '')
    return pathGroupSlug === normalizedGroupSlug && segments[3] === CONTENT_UNAVAILABLE_SEGMENT
  }

  // /[groupSlug]/content-unavailable (GoKollab alias)
  if (segments.length === 2) {
    const pathGroupSlug = normalizeGroupRouteSlug(segments[0] ?? '')
    return pathGroupSlug === normalizedGroupSlug && segments[1] === CONTENT_UNAVAILABLE_SEGMENT
  }

  return false
}

/**
 * Banned members must not see any group content. Returns true when the user's
 * membership row for this group is `BANNED` — the middleware uses this to send
 * the user to `content-unavailable` instead of the requested page.
 */
export function isMemberBanned(membership: UserMembershipData | null | undefined): boolean {
  return membership?.status === MemberStatus.BANNED
}

export function getGroupNotFoundPath(isGokollab: boolean): string {
  return isGokollab ? '/' : '/communities/groups/not-found'
}

/** Default path prefix: /communities/groups/[groupSlug]/... */
const DEFAULT_GROUP_PATH_PREFIX = '/communities/groups/'

/**
 * Decode slug from URL path or route param so percent-encoded segments match router-decoded params.
 * Avoids redirect loops when `to.path` keeps `%C3%A8` but `to.params.groupSlug` is already UTF-8.
 */
export function normalizeGroupRouteSlug(slug: string): string {
  if (!slug || !slug.includes('%')) return slug
  try {
    return decodeURIComponent(slug)
  } catch {
    return slug
  }
}

/**
 * First segment after the dynamic [groupSlug] in path, or undefined if root.
 * Uses path structure (not indexOf) so a slug that matches a static segment
 * (e.g. groupSlug "groups" in /communities/groups/groups/home) is read correctly.
 */
export function getFirstSegmentAfterSlug(path: string, groupSlug: string): string | undefined {
  const normalized = path.replace(/\/+$/, '')
  const segments = normalized.split('/').filter(Boolean)
  const slugIndex = normalized.startsWith(DEFAULT_GROUP_PATH_PREFIX) ? 2 : 0
  const pathSlug = normalizeGroupRouteSlug(segments[slugIndex] ?? '')
  const paramSlug = normalizeGroupRouteSlug(groupSlug)
  if (pathSlug !== paramSlug) return undefined
  return segments[slugIndex + 1]
}

/**
 * True only for membership empty-state **tab index** routes (join/pending UI), not nested
 * post-detail URLs under home. Nested `/home/posts/:id` must still redirect non-members.
 * Channel routes are never empty-state allowlisted — non-members bounce to about.
 */
export function isMembershipEmptyStateTabPath(path: string, groupSlug: string): boolean {
  const normalized = path.replace(/\/+$/, '')
  const segments = normalized.split('/').filter(Boolean)
  const slugIndex = normalized.startsWith(DEFAULT_GROUP_PATH_PREFIX) ? 2 : 0
  const pathSlug = normalizeGroupRouteSlug(segments[slugIndex] ?? '')
  const paramSlug = normalizeGroupRouteSlug(groupSlug)
  if (pathSlug !== paramSlug) return false

  const afterSlug = segments.slice(slugIndex + 1)
  const first = afterSlug[0]
  if (!first || !MEMBERSHIP_EMPTY_STATE_SEGMENTS.has(first)) return false

  // home | learning | events | leaderboard — exact tab only (no nested posts/children)
  return afterSlug.length === 1
}

/**
 * Path is valid group route: has a first segment that is in VALID_GROUP_FIRST_SEGMENTS.
 * Root (no segment) or unknown segment → invalid.
 */
export function isValidGroupPath(path: string, groupSlug: string): boolean {
  const first = getFirstSegmentAfterSlug(path, groupSlug)
  if (first === undefined) return false
  return VALID_GROUP_FIRST_SEGMENTS.has(first)
}

export function hasAccessToGroup(
  group: GroupData['groupDetail'] | null | undefined,
  membership: UserMembershipData | null | undefined,
  isAuthenticated: boolean
): boolean {
  if (!group?.settings?.groupType) return true
  if (group.settings.groupType === GroupType.PUBLIC) return true
  if (group.settings.groupType !== GroupType.PRIVATE) return true
  if (!isAuthenticated) return false
  return membership?.status === MemberStatus.ACTIVE
}

export interface GetGroupRouteRedirectParams {
  path: string
  groupSlug: string
  group: GroupData | null
  membership: UserMembershipData | null
  isAuthenticated: boolean
  /**
   * True when the user is already navigating inside this group (e.g. About → Discussion).
   * Non-members may open empty-state tab indexes only then — cold/direct entry always
   * lands on About first so they see what the community is about.
   */
  isSameGroupNavigation?: boolean
}

/**
 * Returns redirect target or null if allow. accessDenied true when redirecting to about due to no access.
 *
 * Banned members are routed to `content_unavailable` for every in-group path
 * except the content-unavailable page itself (prevents redirect loop). This
 * runs before the public/private + has-access branches because a Banned status
 * always takes priority, even on a public group.
 */
export function getGroupRouteRedirect(params: GetGroupRouteRedirectParams): GroupRouteRedirect | null {
  const { path, groupSlug, group, membership, isAuthenticated, isSameGroupNavigation = false } = params

  const groupDetail = group?.groupDetail ?? null
  if (!groupDetail) {
    return { redirect: 'not_found' }
  }

  const firstSegment = getFirstSegmentAfterSlug(path, groupSlug)

  // Banned member: redirect every in-group route to content-unavailable.
  // Allow content-unavailable itself so the page can render without looping.
  if (isMemberBanned(membership) && firstSegment !== CONTENT_UNAVAILABLE_SEGMENT) {
    return { redirect: 'content_unavailable' }
  }

  // Non-banned users have no reason to land on content-unavailable; send them home.
  if (firstSegment === CONTENT_UNAVAILABLE_SEGMENT) {
    return isMemberBanned(membership) ? null : { redirect: 'home' }
  }

  const hasAccess = hasAccessToGroup(groupDetail, membership, isAuthenticated)
  const validPath = isValidGroupPath(path, groupSlug)

  // About page: always allow (avoid redirect loop)
  if (firstSegment === 'about') return null
  if (firstSegment === 'not-found') return null

  // Plan picker: the one in-group page a non-member is meant to reach — it is how a private
  // paid community is bought into. Banned members were already turned away above.
  if (firstSegment === JOIN_PLANS_ROUTE_SEGMENT) return null

  // Invalid route (root or unknown segment): redirect without accessDenied so no toast
  if (!validPath) {
    return hasAccess ? { redirect: 'home' } : { redirect: 'about' }
  }

  // Non-members: cold/direct entry → About first. After they've entered the group
  // (same-session nav from About, etc.), empty-state tab indexes are allowed.
  if (!hasAccess) {
    if (isSameGroupNavigation && isMembershipEmptyStateTabPath(path, groupSlug)) {
      return null
    }
    return { redirect: 'about', accessDenied: true }
  }

  return null
}
